"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Toggle } from "@/components/ui/toggle";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Repeat, ChevronLeft, ChevronRight, Bitcoin } from "lucide-react";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton, ProductSortDropdown, ProductsFilter } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function ProductsGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden border-border/40 shadow-sm">
            <div className="aspect-square bg-muted/20 animate-pulse" />
            <CardHeader className="space-y-2">
              <div className="h-4 w-2/3 rounded bg-muted/40 animate-pulse" />
              <div className="h-4 w-1/2 rounded bg-muted/40 animate-pulse" />
            </CardHeader>
            <CardContent>
              <div className="h-8 w-full rounded bg-muted/40 animate-pulse" />
            </CardContent>
            <CardFooter>
              <div className="h-10 w-full rounded bg-muted/40 animate-pulse" />
            </CardFooter>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground mb-4">Failed to load marine parts.</p>
        <Button variant="outline" onClick={refetch} className="border-primary text-primary hover:bg-primary/5">
          Try Again
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="flex flex-col items-center justify-center py-24 text-center border rounded-md border-dashed border-border bg-muted/10">
        <p className="text-lg font-default font-medium text-foreground">No spare parts found.</p>
        <p className="text-sm text-muted-foreground mt-1">Try adjusting your filters or search criteria for Yamaha compatibility.</p>
      </div>;
  }
  return <div className="space-y-8">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => <Card key={product.id} data-index={i} className="group overflow-hidden border-border/60 shadow-sm hover:shadow-md hover:scale-105 transition-all duration-300 flex flex-col bg-card rounded-md">
            <Link to={product.link} className="block relative aspect-square overflow-hidden bg-muted/5">
              {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-muted/20 text-muted-foreground font-mono text-xs">
                  NO_IMAGE_DATA
                </div>}
              
              <div className="absolute top-3 left-3 bg-primary text-primary-foreground text-[10px] font-mono font-bold px-2 py-1 rounded-sm shadow-sm tracking-wider uppercase">
                OEM Quality
              </div>
            </Link>

            <CardHeader className="flex-none pb-2">
              <Link to={product.link}>
                <CardTitle className="text-lg font-default font-bold line-clamp-1 group-hover:text-primary transition-colors uppercase tracking-tight">
                  {product.title}
                </CardTitle>
              </Link>
              <div className="flex flex-col gap-1 mt-1">
                <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">Part No: {product.id.toString().padStart(6, '0')}</span>
                <span className="text-xs font-default font-medium text-primary/80">Compatible with Yamaha Outboards</span>
              </div>
            </CardHeader>

            <CardContent className="flex-grow flex flex-col justify-end space-y-4 pb-4">
              <div className="flex items-center justify-between">
                {product.price !== undefined && <div className="flex items-center gap-1.5 text-xl font-mono font-bold text-foreground">
                    <span className="text-sm font-sans text-muted-foreground">$</span>
                    <span>{product.price.toFixed(2)}</span>
                  </div>}
              </div>

              <Toggle variant="outline" className="w-full justify-start rounded-sm border-border/60 data-[state=on]:bg-primary/5 data-[state=on]:text-primary data-[state=on]:border-primary/40 transition-colors text-xs font-default" aria-label="Bulk Order Discount">
                <Repeat className="w-3.5 h-3.5 mr-2 text-secondary" />
                Bulk Inquiry Discount
              </Toggle>
            </CardContent>

            <CardFooter className="pt-0">
              <AddToCartButton productId={product.id} className="w-full" buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm rounded-md font-default font-semibold uppercase tracking-wide text-xs py-5" text="Request Quotation" showQuantity={false} />
            </CardFooter>
          </Card>)}
      </div>

      {(hasNext || hasPrev) && <div className="flex items-center justify-between border-t border-border/40 pt-6">
          <p className="text-xs font-mono text-muted-foreground uppercase tracking-widest">
            Page {currentPage} / {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrev} className="shadow-sm border-border/60 hover:border-primary hover:text-primary rounded-sm">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Prev
            </Button>
            <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNext} className="shadow-sm border-border/60 hover:border-primary hover:text-primary rounded-sm">
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>}
    </div>;
}
export default function ShopContent() {
  const currentWPQueryParams = typeof window !== 'undefined' && (window as any).wvcClient ? (window as any).wvcClient.getCurrentWPQueryParams() : {};
  const wp_query = new WP_Query(currentWPQueryParams);
  return <section data-section-id="222353" id="shop-content" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-8 md:mb-12 border-l-4 border-primary pl-6">
          <h1 className="text-3xl md:text-5xl font-default font-bold text-primary tracking-tighter uppercase">
            Marine Parts Catalog
          </h1>
          <p className="mt-3 text-base md:text-lg text-muted-foreground max-w-2xl font-default leading-relaxed">
            Engineered for reliability. Explore our premium selection of Japanese OEM standard spare parts, fully compatible with Yamaha and leading outboard brands.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={wp_query}>
          <div className="flex flex-col lg:flex-row gap-8">
            
            <aside className="w-full lg:w-1/4 shrink-0 space-y-6">
              <div className="sticky top-24 rounded-md border border-border/60 bg-card p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-1.5 h-6 bg-secondary rounded-lg" />
                  <h2 className="font-default font-bold text-lg text-foreground uppercase tracking-tight">Technical Filters</h2>
                </div>
                <ProductsFilter />
              </div>
              <div className="hidden lg:block p-6 bg-primary/5 rounded-md border border-primary/10">
                <h3 className="text-xs font-mono font-bold text-primary uppercase mb-2">OEM Compatibility</h3>
                <p className="text-[11px] text-muted-foreground leading-tight">All parts listed are manufactured to meet or exceed original equipment manufacturer specifications for marine environments.</p>
              </div>
            </aside>

            
            <div className="w-full lg:w-3/4 flex flex-col space-y-6">
              <div className="flex items-center justify-between bg-muted/40 p-4 rounded-md border border-border/40">
                <p className="text-xs font-mono text-muted-foreground uppercase tracking-widest hidden sm:block">
                  Inventory Status: <span className="text-primary font-bold">Active</span>
                </p>
                <div className="ml-auto">
                  <ProductSortDropdown label="Sort Inventory:" />
                </div>
              </div>
              
              <ProductsGridConsumer />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>;
}